import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
// Screens
import Landing from "./screens/Landing.jsx";
import CardsPage from "./screens/CardsPage.jsx";
import JournalPage from "./screens/JournalPage.jsx";
import VisionBoardPage from "./screens/VisionBoardPage.jsx";
import AboutPage from "./screens/AboutPage.jsx";
import ContactPage from "./screens/ContactPage.jsx";
import WorkshopsPage from "./screens/WorkshopsPage.jsx";
import PrivacyPage from "./screens/PrivacyPage.jsx";
import TermsPage from "./screens/TermsPage.jsx";
import AccessibilityPage from "./screens/AccessibilityPage.jsx";
// Elements
import AccessibilityWidget from "./components/Elements/AccessibilityWidget.jsx";
import CookieBanner from "./components/Elements/CookieBanner.jsx";

export default function App() {
  const [currentPage, setCurrentPage] = useState("home");

  const navigate = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "instant" });
  };

  // ── Click ripple on every button ──
  useEffect(() => {
    const onClick = (e) => {
      const btn = e.target.closest("button");
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const ripple = document.createElement("span");
      ripple.className = "ripple";
      ripple.style.width = ripple.style.height = `${size}px`;
      ripple.style.left = `${e.clientX - rect.left - size / 2}px`;
      ripple.style.top = `${e.clientY - rect.top - size / 2}px`;
      btn.appendChild(ripple);
      ripple.addEventListener("animationend", () => ripple.remove());
    };
    document.addEventListener("pointerdown", onClick);
    return () => document.removeEventListener("pointerdown", onClick);
  }, []);

  // ── Scroll reveal: observe all sections + stagger grids ──
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const el = entry.target;
            el.classList.add("in-view");
            observer.unobserve(el); // fire once
            // after the stagger finishes, drop the delays so hovers feel instant
            if (el.classList.contains("stagger-reveal")) {
              setTimeout(() => el.classList.add("revealed"), 1600);
            }
          }
        });
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" }
    );

    const SELECTOR = "section, .stagger-reveal, .slide-left, .slide-right";
    const observeAll = () =>
      document
        .querySelectorAll(SELECTOR)
        .forEach((el) => !el.classList.contains("in-view") && observer.observe(el));

    // Safety net: anything already on screen is revealed even if the observer misses it
    const revealVisible = () =>
      document.querySelectorAll(SELECTOR).forEach((el) => {
        if (!el.classList.contains("in-view") && el.getBoundingClientRect().top < window.innerHeight) {
          el.classList.add("in-view");
        }
      });
    window.addEventListener("scroll", revealVisible, { passive: true });
    const safety = setTimeout(revealVisible, 1200);

    // Observe after React has painted the new page, then switch reveals on
    const raf = requestAnimationFrame(() => {
      observeAll();
      document.documentElement.classList.add("reveal-on");
    });

    // Elements re-mounted later (hot reload, conditional renders) would otherwise stay hidden
    const mutations = new MutationObserver(observeAll);
    mutations.observe(document.getElementById("root"), { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(safety);
      window.removeEventListener("scroll", revealVisible);
      mutations.disconnect();
      observer.disconnect();
    };
  }, [currentPage]);

  return (
    <>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
        <link href="https://fonts.googleapis.com/css2?family=Assistant:wght@300;400;500;600;700&family=Fredoka:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </Helmet>

      <div key={currentPage} className="page-enter">
        {currentPage === "home"          && <Landing navigate={navigate} />}
        {currentPage === "cards"         && <CardsPage navigate={navigate} />}
        {currentPage === "journal"       && <JournalPage navigate={navigate} />}
        {currentPage === "visionboard"   && <VisionBoardPage navigate={navigate} />}
        {currentPage === "about"         && <AboutPage navigate={navigate} />}
        {currentPage === "contact"       && <ContactPage navigate={navigate} />}
        {currentPage === "workshops"     && <WorkshopsPage navigate={navigate} />}
        {currentPage === "privacy"       && <PrivacyPage navigate={navigate} />}
        {currentPage === "terms"         && <TermsPage navigate={navigate} />}
        {currentPage === "accessibility" && <AccessibilityPage navigate={navigate} />}
      </div>

      {/* Always-visible global elements */}
      <AccessibilityWidget />
      <CookieBanner navigate={navigate} />
    </>
  );
}
